import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, ShoppingCart, User, Menu, X, Phone, ChevronDown, Zap, Heart, CreditCard } from 'lucide-react';
import { useFavorites } from '@/context/FavoritesContext';
import { useCart } from '@/context/CartContext';
import SearchSuggestions from '@/components/search/SearchSuggestions';
import CSAnimatedLogo from '@/components/layout/CSAnimatedLogo';
import { CATEGORIES as CATEGORY_LIST, categoryShopPath, categoryUrlSlug } from '@/utils/categories';

const CATEGORIES = CATEGORY_LIST.map(c => ({
  label: c.label,
  value: c.value,
  slug: categoryUrlSlug(c.value),
  path: categoryShopPath(c.value),
  sub: c.subs.map(s => ({ label: s.label, value: s.value, path: categoryShopPath(c.value, s.value) })),
}));


export default function CSNavbar() {
  const { itemCount, setIsOpen } = useCart();
  const { favorites } = useFavorites();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  return (
    <header className="sticky top-0 z-[100] w-full">
      {/* Top info bar */}
      <div className="bg-cs-dark text-white text-xs py-1 px-2 md:px-4 relative z-10">
        <div className="max-w-screen-xl mx-auto flex justify-between items-center gap-2">
          <span className="flex items-center gap-1 truncate min-w-0"><Zap className="w-3 h-3 text-yellow-400 shrink-0" /> <span className="hidden sm:inline">Livraison rapide 24/72h</span><span className="sm:hidden">24/72h</span></span>
          <span className="hidden md:flex items-center gap-1.5 truncate min-w-0 text-yellow-400 font-semibold">
            <CreditCard className="w-3 h-3 text-yellow-400 shrink-0" />
            <span className="truncate">Paiement CB / Apple Pay / PayPal / Alma 2, 3 ou 4 fois / Virement</span>
          </span>
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <a href="tel:+33651714655" className="hidden md:flex items-center gap-1 hover:text-blue-300 transition-colors">
              <Phone className="w-3 h-3" /> 06 51 71 46 55
            </a>
            <Link to="/contact" className="inline-flex items-center min-h-[36px] px-2 hover:text-blue-300 transition-colors">Contact</Link>
            <Link to="/profil" aria-label="Mon compte" className="inline-flex items-center justify-center min-h-[36px] min-w-[36px] px-2 hover:text-blue-300 transition-colors">
              <User className="w-4 h-4" />
            </Link>
            <Link to="/favoris" className="inline-flex items-center gap-1 min-h-[36px] px-2 hover:text-blue-300 transition-colors">
              <Heart className="w-4 h-4" /> Favoris
            </Link>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-screen-xl mx-auto px-2 py-3 flex items-center gap-2 md:px-4 md:gap-4">
          {/* Logo */}
          <Link to="/" aria-label="Accueil Circuit-Slot" className="flex items-center gap-2.5 shrink-0">
            <img
              src="https://base44.app/api/apps/69f6f4084329a31d2c3702bc/files/mp/public/69f6f4084329a31d2c3702bc/813baf39d_nav-logo-128.webp"
              srcSet="https://base44.app/api/apps/69f6f4084329a31d2c3702bc/files/mp/public/69f6f4084329a31d2c3702bc/1cd6cf1bb_nav-logo-64.webp 1x, https://base44.app/api/apps/69f6f4084329a31d2c3702bc/files/mp/public/69f6f4084329a31d2c3702bc/813baf39d_nav-logo-128.webp 2x"
              alt=""
              width="64"
              height="64"
              className="w-14 h-14 md:w-16 md:h-16 object-contain"
            />
            <CSAnimatedLogo />
          </Link>

          {/* Search bar with suggestions */}
          <SearchSuggestions className="flex-1 max-w-xl mx-auto hidden md:flex" />

          {/* Trust badges */}
          <div className="hidden lg:flex items-center gap-6 text-xs text-gray-600">
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center text-cs-blue text-base">🔒</div>
              <div>
                <div className="font-semibold text-gray-700">Paiement sécurisé</div>
                <div className="text-gray-600">SSL & 3D Secure</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              
              <div>
                
                
              </div>
            </div>
          </div>

          {/* Favorites — desktop only */}
          <Link to="/favoris" aria-label={`Favoris (${favorites.length} articles)`} className="relative shrink-0 hidden md:flex items-center gap-2 text-gray-600 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors">
            <Heart className="w-4 h-4" />
            <span className="text-xs sm:text-sm font-medium">Favoris</span>
            {favorites.length > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {favorites.length}
              </span>
            )}
          </Link>

          {/* Mobile search btn */}
          <button onClick={() => setMobileSearchOpen(!mobileSearchOpen)} aria-label="Rechercher" className="md:hidden shrink-0 p-2 text-gray-600">
            {mobileSearchOpen ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
          </button>

          {/* Cart */}
          <button onClick={() => setIsOpen(true)} aria-label={`Panier (${itemCount} articles)`} className="relative shrink-0 flex items-center gap-2 bg-cs-blue text-white px-3 py-2 rounded-md hover:bg-cs-blue-dark transition-colors">
            <ShoppingCart className="w-4 h-4" />
            <span className="hidden sm:inline text-xs sm:text-sm font-medium">Panier</span>
            {itemCount > 0 &&
            <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {itemCount}
              </span>
            }
          </button>

          {/* Mobile menu btn */}
          <button onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"} className="md:hidden shrink-0 p-2 text-gray-600">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile search dropdown */}
        {mobileSearchOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white px-4 py-3">
            <form onSubmit={handleSearch} className="flex">
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un produit..."
                autoFocus
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md text-sm focus:outline-none focus:border-cs-blue" />
              <button type="submit" aria-label="Rechercher" className="bg-cs-blue text-white px-4 rounded-r-md">
                <Search className="w-4 h-4" />
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Nav bar */}
      <nav className="bg-cs-blue hidden md:block">
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="flex items-center">
            <Link to="/" aria-label="Accueil" className="px-4 py-3 text-white hover:bg-white/10 transition-colors">
              <span className="font-display font-bold text-sm text-white">ACCUEIL</span>
            </Link>
            {CATEGORIES.map((cat) =>
              cat.sub ? (
                <div key={cat.value} className="relative group">
                  <Link
                    to={cat.path}
                    className={`flex items-center gap-1 px-4 py-3 text-sm font-medium text-white hover:bg-white/10 transition-colors whitespace-nowrap ${location.search.includes(cat.slug) ? 'bg-white/20' : ''}`}
                  >
                    {cat.label.toUpperCase()} <ChevronDown className="w-3 h-3" />
                  </Link>
                  <div className="absolute top-full left-0 bg-white shadow-lg rounded-b-lg min-w-[160px] hidden group-hover:block z-50">
                    {cat.sub.map(s => (
                      <Link
                        key={s.value}
                        to={s.path}
                        className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-cs-blue/10 hover:text-cs-blue transition-colors whitespace-nowrap"
                      >
                        {s.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={cat.value}
                  to={cat.path}
                  className={`px-4 py-3 text-sm font-medium text-white hover:bg-white/10 transition-colors whitespace-nowrap ${location.search.includes(cat.slug) ? 'bg-white/20' : ''}`}
                >
                  {cat.label.toUpperCase()}
                </Link>
              )
            )}
            <div className="ml-auto flex items-center">
              <Link to="/a-propos" aria-label="À propos" className="px-4 py-3 text-sm font-medium text-white hover:bg-white/10 transition-colors">
                À PROPOS
              </Link>
              <Link to="/blog" aria-label="Blog" className="px-4 py-3 text-sm font-medium text-white hover:bg-white/10 transition-colors">
                BLOGS
              </Link>
              <Link to="/shop?promo=true" aria-label="Promotions" className="px-4 py-3 text-sm font-bold text-red-100 hover:bg-white/10 transition-colors">
                PROMO 🔥
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Nav */}
      {mobileOpen &&
      <div className="md:hidden bg-white border-b border-gray-200 shadow-lg">
          <div className="px-4 py-3">
            {CATEGORIES.map((cat) => (
              <React.Fragment key={cat.value}>
                <Link
                  to={cat.path}
                  onClick={() => setMobileOpen(false)}
                  className="block py-3 border-b border-gray-100 text-sm font-medium text-gray-700 hover:text-cs-blue transition-colors"
                >
                  {cat.label}
                </Link>
                {cat.sub?.map(s => (
                  <Link
                    key={s.value}
                    to={s.path}
                    onClick={() => setMobileOpen(false)}
                    className="block py-2.5 pl-6 border-b border-gray-50 text-sm text-gray-500 hover:text-cs-blue transition-colors"
                  >
                    ↳ {s.label}
                  </Link>
                ))}
              </React.Fragment>
            ))}
            <Link to="/a-propos" onClick={() => setMobileOpen(false)} className="block py-3 border-b border-gray-100 text-sm font-medium text-gray-700 hover:text-cs-blue transition-colors">
              À propos
            </Link>
            <Link to="/blog" onClick={() => setMobileOpen(false)} className="block py-3 border-b border-gray-100 text-sm font-medium text-gray-700 hover:text-cs-blue transition-colors">
              Blog & Actualités
            </Link>
            <Link to="/contact" onClick={() => setMobileOpen(false)} className="block py-3 border-b border-gray-100 text-sm font-medium text-gray-700 hover:text-cs-blue transition-colors">
              Contact
            </Link>
          </div>
        </div>
      }
    </header>);

}